import {
  CommunicationChannel,
  DeliveryMode,
  SummaryFrequency,
  toUsTimezone,
  WeekDay,
} from "../../constants/communicationConstants";
import {
  ChannelFlags,
  CommunicationDefaults,
  DailyDigest,
  DailyDigestItem,
  DeliverySettings,
  NotificationsDigestApiItem,
  NotificationsDigestResponse,
  PreferenceSchedule,
  SaveDefaultsPayload,
  SaveOrganizationPreferencePayload,
} from "../../services/models/communication/communicationPreferences.model";

export function hasVerifiedMobile(
  mobilePhone: string | null | undefined
): boolean {
  return !!mobilePhone && mobilePhone.replace(/\D/g, "").length >= 10;
}

export function hasValidFax(fax: string | null | undefined): boolean {
  return !!fax && fax.replace(/\D/g, "").length >= 10;
}

export function toggleChannel(
  channels: CommunicationChannel[],
  channel: CommunicationChannel
): CommunicationChannel[] {
  if (channels.includes(channel)) {
    return channels.filter((c) => c !== channel);
  }
  return [...channels, channel];
}

export function isChannelSelectable(
  channel: CommunicationChannel,
  contactInfo: {
    mobilePhone?: string | null;
    fax?: string | null;
    email?: string | null;
    mobilePhoneVerified?: boolean;
    hasFaxNumber?: boolean;
  }
): { enabled: boolean; statusLabel?: string; showVerifyLink?: boolean } {
  if (channel === CommunicationChannel.EMAIL) {
    return {
      enabled: true,
      statusLabel: contactInfo.email || "Registered email",
    };
  }

  if (channel === CommunicationChannel.SMS) {
    const hasPhone = hasVerifiedMobile(contactInfo.mobilePhone);
    if (!hasPhone) {
      return {
        enabled: false,
        statusLabel: "No mobile number",
        showVerifyLink: true,
      };
    }
    if (contactInfo.mobilePhoneVerified !== true) {
      return {
        enabled: false,
        statusLabel: contactInfo.mobilePhone ?? "Unverified mobile",
        showVerifyLink: true,
      };
    }
    return {
      enabled: true,
      statusLabel: contactInfo.mobilePhone ?? "Verified mobile",
    };
  }

  if (channel === CommunicationChannel.FAX) {
    const hasFax =
      contactInfo.hasFaxNumber ?? hasValidFax(contactInfo.fax);
    if (!hasFax) {
      return {
        enabled: false,
        statusLabel: "No fax number on file",
      };
    }
    return {
      enabled: true,
      statusLabel: "Number on file",
    };
  }

  return { enabled: true };
}

export function channelsFromFlags(flags: ChannelFlags): CommunicationChannel[] {
  const channels: CommunicationChannel[] = [];
  if (flags.emailEnabled) channels.push(CommunicationChannel.EMAIL);
  if (flags.smsEnabled) channels.push(CommunicationChannel.SMS);
  if (flags.faxEnabled) channels.push(CommunicationChannel.FAX);
  return channels;
}

export function flagsFromChannels(channels: CommunicationChannel[]): ChannelFlags {
  return {
    emailEnabled: channels.includes(CommunicationChannel.EMAIL),
    smsEnabled: channels.includes(CommunicationChannel.SMS),
    faxEnabled: channels.includes(CommunicationChannel.FAX),
  };
}

export function scheduleFromApi(
  flags: ChannelFlags,
  delivery: DeliverySettings
): PreferenceSchedule {
  return {
    channels: channelsFromFlags(flags),
    deliveryMode: delivery.deliveryMode ?? DeliveryMode.REAL_TIME,
    summaryTime: delivery.scheduleTime ?? "08:00",
    summaryFrequency: delivery.scheduleFrequency ?? SummaryFrequency.DAILY,
    summaryDayOfWeek: delivery.scheduleDayOfWeek ?? WeekDay.MONDAY,
    timezone: toUsTimezone(
      delivery.scheduleTimezone ??
        Intl.DateTimeFormat().resolvedOptions().timeZone
    ),
  };
}

export function scheduleFromDefaults(
  defaults: CommunicationDefaults
): PreferenceSchedule {
  return scheduleFromApi(defaults, defaults);
}

export function toSaveDefaultsPayload(
  schedule: PreferenceSchedule
): SaveDefaultsPayload {
  const flags = flagsFromChannels(schedule.channels);
  if (schedule.deliveryMode === DeliveryMode.REAL_TIME) {
    return {
      ...flags,
      deliveryMode: DeliveryMode.REAL_TIME,
    };
  }

  return {
    ...flags,
    deliveryMode: DeliveryMode.SUMMARY,
    scheduleFrequency: schedule.summaryFrequency,
    scheduleDayOfWeek:
      schedule.summaryFrequency === SummaryFrequency.WEEKLY
        ? schedule.summaryDayOfWeek
        : null,
    scheduleTime: schedule.summaryTime,
    scheduleTimezone: toUsTimezone(schedule.timezone),
  };
}

export function toSaveOrganizationPayload({
  usesPersonalDefaults,
  externalNotificationsEnabled,
  schedule,
}: {
  usesPersonalDefaults: boolean;
  externalNotificationsEnabled: boolean;
  schedule?: PreferenceSchedule;
}): SaveOrganizationPreferencePayload {
  if (usesPersonalDefaults || !schedule) {
    return {
      usesPersonalDefaults: true,
      externalNotificationsEnabled,
    };
  }

  const defaultsPayload = toSaveDefaultsPayload(schedule);
  return {
    usesPersonalDefaults: false,
    externalNotificationsEnabled,
    ...defaultsPayload,
  };
}

export function formatChannelsLabel(channels: CommunicationChannel[]): string {
  if (channels.length === 0) {
    return "None selected";
  }
  return channels
    .map((c) => {
      switch (c) {
        case CommunicationChannel.EMAIL:
          return "Email";
        case CommunicationChannel.SMS:
          return "SMS";
        case CommunicationChannel.FAX:
          return "Fax";
        default:
          return c;
      }
    })
    .join(", ");
}

export function formatDeliveryLabel({
  deliveryMode,
  summaryFrequency,
  summaryTime,
  summaryDayOfWeek,
  timezone,
}: {
  deliveryMode: string;
  summaryFrequency?: string | null;
  summaryTime?: string | null;
  summaryDayOfWeek?: string | null;
  timezone?: string | null;
}): string {
  if (deliveryMode === DeliveryMode.REAL_TIME || deliveryMode === "realtime") {
    return "As they happen";
  }
  const freq = summaryFrequency === "weekly" ? "Weekly" : "Daily";
  const day =
    summaryFrequency === "weekly" && summaryDayOfWeek
      ? ` ${summaryDayOfWeek.charAt(0).toUpperCase()}${summaryDayOfWeek.slice(1)}`
      : "";
  return `${freq}${day} at ${summaryTime ?? "08:00"} (${timezone ?? "local"})`;
}

export function getApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return err?.response?.data?.message ?? err?.message ?? fallback;
}

function mapDigestApiItem(item: NotificationsDigestApiItem): DailyDigestItem {
  const typeKey = item.notificationType?.key ?? item.contentType ?? "";
  const isActionRequired =
    typeKey.toUpperCase().includes("APPROVAL") ||
    typeKey.toUpperCase().includes("MISSING") ||
    typeKey.toUpperCase().includes("ATTESTATION");

  return {
    id: String(item.id),
    type: typeKey,
    title: item.notificationType?.name ?? "Notification",
    description: item.description ?? "",
    priority: isActionRequired ? "action_required" : "informational",
    deepLink: item.notificationType?.frontEndURL
      ? `/access${item.notificationType.frontEndURL}`
      : "/access/notifications",
    timestamp: item.createdAt,
  };
}

/** Maps GET /v1/notifications/digest response into the Daily Digest UI model. */
export function mapNotificationsDigestToDailyDigest(
  data: NotificationsDigestResponse
): DailyDigest {
  const held = (data.held ?? []).map(mapDigestApiItem);
  const recentlyDigested = (data.recentlyDigested ?? []).map(mapDigestApiItem);
  const allItems = [...held, ...recentlyDigested];
  const pendingApprovalCount = allItems.filter(
    (item) => item.priority === "action_required"
  ).length;

  return {
    date: new Date().toISOString(),
    pendingApprovalCount,
    isEmpty: allItems.length === 0,
    sections: [
      {
        key: "held",
        title: "Waiting for next digest",
        items: held,
      },
      {
        key: "recentlyDigested",
        title: "Recently digested",
        items: recentlyDigested,
      },
    ],
  };
}
